import { layOutOptions, mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * Shadow matching — which animal cast this shadow?
 *
 * A flat silhouette above, coloured animals below, and the child picks the one whose outline it is.
 * The activity a preschool worksheet calls سایه‌یابی, and the one thing in this bundle that asks a
 * child to recognise something with every cue but its OUTLINE taken away: no colour, no markings,
 * no face. That is a real perceptual step and nothing else here trains it — matching a pattern
 * compares two things drawn the same way, and spot-the-difference compares two things that are
 * almost identical.
 *
 * **This vocabulary is deliberately NOT the token icons in `sequence.ts`.** Those exist under the
 * opposite requirement: their comment says silhouettes are kept distinct on purpose, because
 * spot-the-difference changes shape alone at its easier levels and two similar outlines would turn
 * those levels into a colour task. Here confusable outlines ARE the difficulty axis, so sharing one
 * list would mean one of the two games getting the shapes the other needed. Two vocabularies, each
 * with a written reason, beats one that quietly serves neither.
 *
 * **Difficulty is who else is in the row, never how the shadow is drawn.** A silhouette is already
 * the hardest version of itself; shrinking it or turning it would add a second thing to work out
 * (mental rotation) and make a level that is harder for a reason nobody chose. So the ladder adds
 * options first, then fills them with the answer's own look-alikes.
 */
/**
 * Twenty-four animals in eight families of three.
 *
 * **They are real drawings rather than shapes this codebase invented**, and that is the whole
 * difference between a game and a worksheet. The first version of this file had twelve
 * hand-drawn primitives — an apple, a leaf, a star — grouped by a similarity nobody would
 * describe out loud; a child matching a shadow to a lemon-shape does not think they are playing
 * anything. Animals are what the printed version of this activity has always used, and a horse
 * next to a donkey is a comparison a five-year-old will actually stay for.
 *
 * A family is three animals that share an outline at a glance and differ only in detail — which
 * is exactly what a silhouette removes. `apps/web/scripts/make-animal-atlas.py` cuts them out of
 * the source sheets and the renderer draws them; the ORDER below is that script's roster and the
 * two must not drift, because an id is what a recorded trial stores.
 *
 * The families, and why each is genuinely hard: hoofed animals in profile differ by a mane and an
 * ear; antlered ones by what is on the head; the heavy grey ones by a nose; the long-tailed
 * hunters by a tail; perched birds by a beak; standing birds by a neck; sea animals by a fin;
 * and the sprawling many-legged ones by almost nothing at all.
 */
export const SHADOW_SHAPES = 24;
/** How many families there are. Every one holds exactly three, which the top level relies on. */
const FAMILY_COUNT = 8;
/**
 * Which family each animal belongs to, by id.
 *
 * Written out rather than derived from the index so that adding an animal to one family cannot
 * silently renumber another family's members.
 */
export const SHADOW_FAMILY = [
    // Hoofed, in profile: horse, donkey, zebra.
    0, 0, 0,
    // Something on the head: deer, moose, gazelle.
    1, 1, 1,
    // Heavy and grey: elephant, rhino, hippo.
    2, 2, 2,
    // Long body, long tail: wolf, fox, cheetah.
    3, 3, 3,
    // Perched: owl, eagle, toucan.
    4, 4, 4,
    // Standing tall: flamingo, swan, peacock.
    5, 5, 5,
    // Streamlined, in water: whale, dolphin, shark.
    6, 6, 6,
    // Many legs round a small body: scorpion, crab, tarantula.
    7, 7, 7,
];
/** The families, as lists of animal ids. */
const FAMILIES = Array.from({ length: FAMILY_COUNT }, (_, family) => SHADOW_FAMILY.map((f, id) => (f === family ? id : -1)).filter((id) => id >= 0));
export const SHADOW_MIN_DIFFICULTY = 1;
export const SHADOW_MAX_DIFFICULTY = 8;
/**
 * Eight levels: cards first, then look-alikes, then a second cluster of them.
 *
 * Six options is the ceiling because of the phone rather than the task — the shared choice renderer
 * derives its columns from the touch floor, so six come out as 3×2 at about 100 px each and eight
 * would come out as 4×2 at 76, exactly on the floor with nothing to spare.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { optionCount: 2, siblings: 0, secondFamily: false };
        case 2:
            return { optionCount: 3, siblings: 0, secondFamily: false };
        case 3:
            return { optionCount: 4, siblings: 0, secondFamily: false };
        // The first look-alike: one option now shares the answer's outline.
        case 4:
            return { optionCount: 4, siblings: 1, secondFamily: false };
        // The answer's whole family is on screen, so outline decides nothing at all.
        case 5:
            return { optionCount: 4, siblings: 2, secondFamily: false };
        case 6:
            return { optionCount: 6, siblings: 1, secondFamily: false };
        case 7:
            return { optionCount: 6, siblings: 2, secondFamily: false };
        default:
            return { optionCount: 6, siblings: 2, secondFamily: true };
    }
}
export function generateShadow(task, formId, trialIndex, difficulty) {
    if (difficulty < SHADOW_MIN_DIFFICULTY || difficulty > SHADOW_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { optionCount, siblings, secondFamily } = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    // The answer's family is drawn first, so which family is the "hard" one changes every trial.
    // Fixing it would make the game learnable as "the answer is always one of the birds".
    const order = shuffle(Array.from({ length: FAMILY_COUNT }, (_, i) => i), rand);
    const answerFamily = order[0];
    const own = shuffle(FAMILIES[answerFamily] ?? [], rand);
    const answer = own[0];
    const chosen = own.slice(1, 1 + siblings);
    if (secondFamily) {
        const other = shuffle(FAMILIES[order[1]] ?? [], rand);
        chosen.push(...other.slice(0, optionCount - 1 - chosen.length));
    }
    else {
        /**
         * Round-robin over the other families, so the fillers spread instead of piling up.
         *
         * Six options with one sibling needs four fillers out of three remaining families, so one of
         * them contributes twice however this is written. Taking them in turn means the doubled family
         * is a different one each trial rather than always the first one drawn.
         */
        const pools = order.slice(1).map((family) => shuffle(FAMILIES[family] ?? [], rand));
        for (let round = 0; chosen.length < optionCount - 1; round++) {
            for (const pool of pools) {
                if (chosen.length >= optionCount - 1)
                    break;
                const next = pool[round];
                if (next !== undefined)
                    chosen.push(next);
            }
            // Every pool is three deep and the largest fill is four, so this cannot spin. Guarded
            // anyway: a recipe that asked for more options than there are shapes should throw rather
            // than hang a child's phone.
            if (round > SHADOW_SHAPES) {
                throw new Error(`cannot fill ${optionCount} options at difficulty ${difficulty}`);
            }
        }
    }
    // The whole row is laid out together rather than the answer being spliced into a built list —
    // see `layOutOptions` for the leak that fixes.
    const { options, correctIndex } = layOutOptions(answer, chosen, rand);
    return {
        target: [answer],
        options: options.map((shape) => [shape]),
        correctIndex,
    };
}
/** Matching scoring: the chosen card must be the one that casts the shadow. */
export function scoreShadowTrial(correctIndex, chosenIndex) {
    return correctIndex === chosenIndex;
}
//# sourceMappingURL=shadow.js.map